import { PERMISSIONS } from "@/constants/permissions"
import { useGuardedMutation } from "@/hooks/useGuardedMutation"
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import { apiClientV2 } from "../../../services/api/apiClient"
import {
  mcCursorList,
  mcCursorListKeepPrevious,
  useMcPatch,
} from "./mcQueryHelpers"
import type {
  ComplianceEventOut,
  CustomerRow,
  ProductArticleCreateIn,
  ProductArticleRow,
  ProductOut,
  ProductRow,
  ProductUpdateIn,
} from "./types/masterData"

const BASE = "/material-compliance/master-data/products"

// ─── List ─────────────────────────────────────────────────────────────────────

export function useProductList(
  params: {
    q?: string
    customer_id?: string
    supplier_id?: string
    article_id?: string
    substance_id?: string
    regulation_id?: string
    status?: string
    sort?: string
    limit?: number
    no_articles?: boolean
    /** Set false to skip the request entirely, e.g. while a sibling query owns the active view. Defaults to true. */
    enabled?: boolean
  } = {},
) {
  const { enabled = true, ...listParams } = params
  return useInfiniteQuery(
    mcCursorListKeepPrevious<ProductRow>(
      ["mc", "products", "list", listParams],
      BASE,
      listParams,
      enabled,
    ),
  )
}

// ─── Detail ───────────────────────────────────────────────────────────────────

export function useProductDetail(id: string | null) {
  return useQuery({
    queryKey: ["mc", "products", "detail", id],
    queryFn: () =>
      apiClientV2.get<ProductOut>(`${BASE}/${id}`).then((r) => r.data),
    enabled: !!id,
  })
}

// ─── Sub-resource: audit trail ────────────────────────────────────────────────

export function useProductAudit(
  id: string | null,
  params: { limit?: number } = {},
) {
  return useInfiniteQuery(
    mcCursorList<ComplianceEventOut>(
      ["mc", "products", id, "audit", params],
      `${BASE}/${id}/audit`,
      params,
      !!id,
    ),
  )
}

// ─── Sub-resource: articles linked to this product (BOM components) ───────────

export function useProductArticles(
  id: string | null,
  params: { limit?: number } = {},
) {
  return useInfiniteQuery(
    mcCursorList<ProductArticleRow>(
      ["mc", "products", id, "articles", params],
      `${BASE}/${id}/articles`,
      params,
      !!id,
    ),
  )
}

// ─── Sub-resource: customers linked to this product ────────────────────────────

export function useProductCustomers(
  id: string | null,
  params: { limit?: number } = {},
) {
  return useInfiniteQuery(
    mcCursorList<CustomerRow>(
      ["mc", "products", id, "customers", params],
      `${BASE}/${id}/customers`,
      params,
      !!id,
    ),
  )
}

// ─── Sub-resource: products linked to a given article (reverse of useProductArticles) ─

// Deliberately NOT mcCursorListKeepPrevious: this is a per-entity sub-resource
// keyed by articleId, and that helper's contract forbids it there — the
// previous article's product rows (with their unlink buttons still wired to
// the OLD article id) would stay on screen while the new article's page loads.
export function useArticleProducts(
  articleId: string | null,
  params: { limit?: number } = {},
) {
  return useInfiniteQuery(
    mcCursorList<ProductRow>(
      ["mc", "articles", articleId, "products", params],
      BASE,
      { ...params, article_id: articleId ?? undefined },
      !!articleId,
    ),
  )
}

// ─── Mutation ─────────────────────────────────────────────────────────────────

export function usePatchProduct() {
  return useMcPatch<ProductOut, ProductUpdateIn>("products", BASE)
}

// ─── Mutations: product ↔ article manual link (TUR-1518) ──────────────────────

function invalidateProductArticleCaches(
  qc: ReturnType<typeof useQueryClient>,
  productId: string,
  articleId: string,
) {
  qc.invalidateQueries({ queryKey: ["mc", "products", "detail", productId] })
  qc.invalidateQueries({ queryKey: ["mc", "products", productId, "articles"] })
  qc.invalidateQueries({ queryKey: ["mc", "products", productId, "audit"] })
  qc.invalidateQueries({ queryKey: ["mc", "products", "list"] })
  qc.invalidateQueries({ queryKey: ["mc", "articles", "detail", articleId] })
  qc.invalidateQueries({ queryKey: ["mc", "articles", "list"] })
  // Linking/unlinking can move a product's completeness bucket (e.g. out of
  // "no articles"), same as any other master-data write — see
  // invalidateMcEntityCaches, the shared patch path this mirrors.
  qc.invalidateQueries({ queryKey: ["mc", "master-data", "stats"] })
  // Same reasoning for the "Datenlücken" tile (TUR-1539): linking/unlinking
  // an article can move it in or out of a gap bucket.
  qc.invalidateQueries({ queryKey: ["mc", "master-data", "data-gaps"] })
}

export function useAddProductArticle() {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({
      productId,
      articleId,
      quantity,
      unit,
      positionNumber,
    }: { productId: string } & ProductArticleCreateIn) =>
      apiClientV2
        .post<ProductArticleRow>(`${BASE}/${productId}/articles`, {
          article_id: articleId,
          // Only send a key when the caller actually passed it: the backend
          // treats an explicit `null` as "clear this field" (exclude_unset),
          // so defaulting an omitted field to null here would NULL out
          // existing BOM data on every re-link.
          ...(quantity !== undefined ? { quantity } : {}),
          ...(unit !== undefined ? { unit } : {}),
          ...(positionNumber !== undefined
            ? { position_number: positionNumber }
            : {}),
        })
        .then((r) => r.data),
    onSuccess: (_, { productId, articleId }) =>
      invalidateProductArticleCaches(qc, productId, articleId),
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_MASTER_DATA_EDIT)
}

export function useRemoveProductArticle() {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({
      productId,
      articleId,
    }: {
      productId: string
      articleId: string
    }) =>
      apiClientV2
        .delete<{ ok: boolean }>(`${BASE}/${productId}/articles/${articleId}`)
        .then((r) => r.data),
    onSuccess: (_, { productId, articleId }) =>
      invalidateProductArticleCaches(qc, productId, articleId),
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_MASTER_DATA_EDIT)
}
